import { graphql } from 'gatsby';
import getYouTubeId from 'get-youtube-id';
import React, { useRef, useState } from 'react';
import { FaPlayCircle } from 'react-icons/fa';
import { CommonModuleProps } from '../../ModulesContent';
import ModuleLayout from '../ModuleLayout';
import * as styles from './VideoModule.module.scss';

export const VideoModuleFragment = graphql`
  fragment VideoModule on SanityVideoModule {
    youtubeUrl
    videoTitle
    videoDescription
    videoUploadDate
    videoThumbnailUrl
  }
`;

export type VideoModuleProps = {
  youtubeUrl: string;
  videoTitle?: string;
  videoDescription?: string;
  videoUploadDate?: string;
  videoThumbnailUrl?: string;
  className?: string;
};

function VideoModule(props: VideoModuleProps & CommonModuleProps): React.ReactElement {
  const { youtubeUrl, videoTitle, videoThumbnailUrl, className, moduleId } = props;
  const containerRef = useRef<HTMLElement | null>(null);
  const videoId = getYouTubeId(youtubeUrl);
  const [isPlaying, setIsPlaying] = useState(false);

  // Prefer the CMS-supplied thumbnail; fall back to YouTube's hosted one.
  // maxresdefault isn't guaranteed for every video, so hqdefault is the
  // safer default — it exists for every YouTube video.
  const thumbnailUrl =
    videoThumbnailUrl || (videoId && `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`);

  const playLabel = videoTitle ? `Play video: ${videoTitle}` : 'Play video';

  return (
    <ModuleLayout
      id={moduleId}
      ref={containerRef}
      className={className}
      childrenClassName={styles.container}
      currentModuleBgColor={props.theme}
    >
      <div className={styles.videoWrapper}>
        {isPlaying ? (
          <iframe
            className={styles.iframe}
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
            title={videoTitle || 'YouTube video'}
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className={styles.thumbnailButton}
            aria-label={playLabel}
            onClick={() => setIsPlaying(true)}
          >
            {thumbnailUrl && (
              <img src={thumbnailUrl} alt="" loading="lazy" decoding="async" />
            )}
            <FaPlayCircle className={styles.playIcon} aria-hidden="true" />
          </button>
        )}
      </div>
    </ModuleLayout>
  );
}

export default VideoModule;
